import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Play, 
  Heart, 
  Eye, 
  User, 
  Plus, 
  Search,
  Filter,
  X,
  Loader2,
  Video as VideoIcon,
  MessageCircle,
  Settings
} from 'lucide-react';

import { API_URL } from '../utils/api';

function VideoCard({ post, onLike }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [checkingLike, setCheckingLike] = useState(false);
  const videoRef = useRef(null);
  
  // Determine if this is a video or image post
  const isVideo = post.media_type === 'video' || post.video_url;

  // Check if user has liked this post
  useEffect(() => {
    if (user && token) {
      checkLikeStatus();
    }
  }, [user, token, post.id]);

  const checkLikeStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/creator-posts/${post.id}/liked`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLiked(data.liked);
      }
    } catch (error) {
      // Ignore errors
    }
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    setCheckingLike(true);
    try {
      const response = await fetch(`${API_URL}/api/creator-posts/${post.id}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLiked(data.liked);
        setLikeCount(data.like_count);
      }
    } catch (error) {
      console.error('Like error:', error);
    } finally {
      setCheckingLike(false);
    }
  };

  const handlePlay = () => {
    if (!isVideo) return; // Don't do anything for images
    
    // When not playing, we just need to set the state to true
    // The video element will render and autoPlay
    if (!playing) {
      setPlaying(true);
    } else if (videoRef.current) {
      // When already playing, toggle pause/play
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const videoSrc = (post.video_url || post.media_url)?.startsWith('/api') 
    ? `${API_URL}${post.video_url || post.media_url}` 
    : (post.video_url || post.media_url);
    
  const imageSrc = post.media_url?.startsWith('/api')
    ? `${API_URL}${post.media_url}`
    : post.media_url;
    
  const thumbnailSrc = post.thumbnail_url?.startsWith('/api')
    ? `${API_URL}${post.thumbnail_url}`
    : post.thumbnail_url;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-stone-200 hover:shadow-md transition-shadow">
      {/* Media (Video or Image) */}
      <div 
        className={`relative aspect-video bg-stone-900 ${isVideo ? 'cursor-pointer' : ''} group`}
        onClick={isVideo ? handlePlay : undefined}
      >
        {isVideo ? (
          // Video content
          playing ? (
            <video
              ref={videoRef}
              src={videoSrc}
              className="w-full h-full object-contain"
              autoPlay
              onEnded={() => setPlaying(false)}
              onClick={(e) => e.stopPropagation()}
              controls
            />
          ) : (
            <>
              {thumbnailSrc ? (
                <img 
                  src={thumbnailSrc}
                  alt={post.caption}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-800 to-stone-900">
                  <VideoIcon className="h-16 w-16 text-stone-600" />
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="h-8 w-8 text-teal-600 ml-1" fill="currentColor" />
                </div>
              </div>
            </>
          )
        ) : (
          // Image content
          <img 
            src={imageSrc || thumbnailSrc}
            alt={post.caption}
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Creator Info */}
        <Link 
          to={`/profile/${post.creator_id}`}
          className="flex items-center gap-2 mb-2 hover:opacity-80"
        >
          {post.creator_photo ? (
            <img 
              src={post.creator_photo}
              alt={post.creator_name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center">
              <User className="h-4 w-4 text-stone-500" />
            </div>
          )}
          <span className="font-medium text-stone-800 text-sm">{post.creator_name}</span>
        </Link>

        {/* Caption */}
        <p className="text-stone-700 text-sm line-clamp-2 mb-3">{post.caption}</p>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {post.tags.slice(0, 3).map(tag => (
              <span 
                key={tag}
                className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-between text-stone-500 text-sm">
          <button 
            onClick={handleLike}
            disabled={checkingLike}
            className={`flex items-center gap-1 hover:text-teal-600 transition-colors ${
              liked ? 'text-teal-600' : ''
            }`}
          >
            <Heart className={`h-5 w-5 ${liked ? 'fill-current' : ''}`} />
            <span>{likeCount}</span>
          </button>
          
          <div className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            <span>{post.view_count || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreatorFeedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTag, setSearchTag] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const POSTS_PER_PAGE = 12;

  useEffect(() => {
    fetchPosts(0, true);
  }, [searchTag]);

  const fetchPosts = async (skip = 0, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      let url = `${API_URL}/api/creator-feed?limit=${POSTS_PER_PAGE}&skip=${skip}`;
      if (searchTag) {
        url += `&tag=${encodeURIComponent(searchTag)}`;
      }

      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        if (reset) {
          setPosts(data.posts);
        } else {
          setPosts(prev => [...prev, ...data.posts]);
        }
        setHasMore(data.has_more);
        setPage(skip);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    fetchPosts(page + POSTS_PER_PAGE, false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
  };

  const canPost = user && (
    user.subscription_status === 'active' || 
    user.subscription_status === 'trial' ||
    user.is_admin ||
    user.is_tradie
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-sky-500 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Crafters Showroom</h1>
              <p className="text-sky-100 text-lg">
                Discover photos and videos from our talented makers
              </p>
            </div>
            
            {canPost && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 self-start md:self-center w-full sm:w-auto">
                <Button
                  onClick={() => navigate('/creator/post')}
                  className="bg-white text-teal-600 hover:bg-teal-50 w-full sm:w-auto"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Share Your Work
                </Button>
                <Button
                  onClick={() => navigate('/creator/my-videos')}
                  variant="outline"
                  className="border-white/50 text-white hover:bg-white/10 w-full sm:w-auto"
                >
                  <Settings className="h-5 w-5 mr-2" />
                  My Showroom
                </Button>
              </div>
            )}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-6 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-sky-300" />
              <Input
                value={searchTag}
                onChange={(e) => setSearchTag(e.target.value)}
                placeholder="Search by tag (e.g., woodworking, restoration)"
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-sky-200 focus:bg-white/20"
              />
              {searchTag && (
                <button
                  type="button"
                  onClick={() => setSearchTag('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sky-200 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <VideoIcon className="mx-auto h-16 w-16 text-stone-300 mb-4" />
            <h3 className="text-xl font-medium text-stone-700 mb-2">
              {searchTag ? 'No posts found for this tag' : 'No posts yet'}
            </h3>
            <p className="text-stone-500 mb-6">
              {searchTag 
                ? 'Try a different search term'
                : 'Be the first to share your work with the community!'
              }
            </p>
            {canPost && !searchTag && (
              <Button
                onClick={() => navigate('/creator/post')}
                className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
              >
                <Plus className="h-5 w-5 mr-2" />
                Share Your First Post
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Post Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {posts.map(post => (
                <VideoCard key={post.id} post={post} />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-8">
                <Button
                  onClick={loadMore}
                  variant="outline"
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More Videos'
                  )}
                </Button>
              </div>
            )}
          </>
        )}

        {/* CTA for non-subscribers */}
        {!canPost && user && (
          <div className="mt-12 bg-gradient-to-r from-teal-50 to-orange-50 rounded-xl p-8 text-center border border-teal-100">
            <h3 className="text-xl font-bold text-stone-800 mb-2">
              Want to share your creations?
            </h3>
            <p className="text-stone-600 mb-4">
              Subscribe to start posting videos and grow your audience
            </p>
            <Button
              onClick={() => navigate('/subscription')}
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
            >
              Start Your Free Trial
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
